import { Currency, RouteType } from "@daytrip/legacy-enums";
import { Expose, Transform, Type } from "class-transformer";
import {
    IsOptional,
    IsNumber,
    IsArray,
    IsBoolean,
    IsDate,
    IsDefined,
    IsInt,
    IsString,
    IsUUID,
    IsEnum,
    ArrayMinSize,
} from "class-validator";
import { v4 as uuid } from "uuid";

import { GField, GInputType, GInt, GObjectType } from "../graphql";
import { CDBaseEntity, CDColumn, CDEntity, CDObjectIdColumn } from "../orm";
import { TransformToDate } from "../transformers/transformToDate";

import { Country } from "./Country";
import { Location } from "./Location";
import { RouteLocation } from "./RouteLocation";
import { UpsellRoute } from "./UpsellRoute";
import { VehicleTypePriceFee } from "./VehicleTypePriceFee";
import { Waypoint } from "./Waypoint";

@GObjectType("Route")
@GInputType("RouteInput")
@CDEntity("routes")
export class Route extends CDBaseEntity {
    @GField()
    @CDObjectIdColumn()
    @IsUUID("4")
    @Expose()
    public _id: string = uuid();

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isGenerated: boolean = false; // the route was generated automatically and not updated since

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isVisited: boolean = false; // the route has been visited by a customer at least once

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isReviewed: boolean = false; // the route was manually reviewed by a content manager

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isReady: boolean = false;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isLive: boolean = false;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isSuggested: boolean = false;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isApiAvailable: boolean = true;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isAirportTransfer: boolean = false;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isMeetAndGreet: boolean = true;

    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    @GField()
    public originLocationId: string;

    @CDColumn()
    @IsString()
    @Expose()
    public originLocationMachineName: string;

    @GField(() => Location, { nullable: true })
    public originLocation: Location;

    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    @GField()
    public destinationLocationId: string;

    @CDColumn()
    @IsString()
    @Expose()
    public destinationLocationMachineName: string;

    @GField(() => Location, { nullable: true })
    public destinationLocation: Location;

    @GField({ defaultValue: false })
    public isOtherDirection: boolean;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isBidirectional: boolean = true;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public type: RouteType = RouteType.Car;

    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public tollPrice: number = 0;

    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public tollFee: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public additionalPrice: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public additionalFee: number = 0;

    @CDColumn()
    @IsInt()
    @Transform((value) => (value == null ? null : Number(value)))
    @Expose()
    public publicTransportDuration: number | null = null; // null = unknown

    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public pricingCountryId: string;

    @IsOptional()
    @Expose()
    @Type(() => Country)
    public pricingCountry: Country;

    @CDColumn()
    @IsUUID("4")
    @Expose()
    public pricingRegionId?: string;

    @GField(() => GInt)
    @CDColumn()
    @IsEnum(Currency)
    @Expose()
    public pricingCurrency = Currency.Euro;

    @CDColumn()
    @IsString()
    @IsDefined()
    @Expose()
    public note: string = "";

    @GField(() => [VehicleTypePriceFee])
    @CDColumn()
    @IsArray()
    @ArrayMinSize(1)
    @IsDefined()
    @Expose()
    @Type(() => VehicleTypePriceFee)
    public vehicleTypesPricesFees: VehicleTypePriceFee[] = [];

    @GField(() => [Location])
    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => RouteLocation)
    public locations: RouteLocation[] = [];

    @IsArray()
    @IsOptional()
    @Expose()
    @Type(() => Location)
    public stopLocations: Location[] = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Waypoint)
    public waypoints: Waypoint[] = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    public reviewIds: string[] = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    public tagIds: string[] = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => UpsellRoute)
    public upsellRoutes: UpsellRoute[] = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => UpsellRoute)
    public upsellRoutesInOtherDirection: UpsellRoute[] = [];

    @CDColumn()
    @IsInt()
    @Expose()
    public priority: number = 0;

    @GField({ nullable: true })
    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public availableFrom?: Date;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public upsellReturnTrip: boolean = false;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public upsellReturnTripInOtherDirection: boolean = false;

    @CDColumn()
    @IsUUID("4")
    @Expose()
    public createdBy?: string;

    @CDColumn()
    @IsDate()
    @IsDefined()
    @Expose()
    @TransformToDate
    public createdAt: Date = new Date();

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public pricesRecalculatedAt?: Date;
}
